"use client";

import { useState } from "react";
import { usePuck } from "@puckeditor/core";
import { Input } from "@/components/ui/input";
import {
  isDefaultStudioEditingLang,
  useStudioEditing,
} from "@/app/studio/pages/[slug]/StudioEditingContext";
import type { SupportedLanguage } from "@/utils/library/languages";
import { isStudioPendingImageSrc } from "@/utils/studioImageStyles";

type StudioAltFieldProps = {
  value: string;
  onChange: (value: string) => void;
};

export function StudioAltField({
  value,
  onChange,
}: Readonly<StudioAltFieldProps>) {
  const { selectedItem } = usePuck();
  const { editingLang, getEnglishBlockProp, applyBlockPropByLocale } =
    useStudioEditing();
  const [generating, setGenerating] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const src =
    typeof selectedItem?.props?.src === "string"
      ? selectedItem.props.src
      : "";
  const blockId =
    typeof selectedItem?.props?.id === "string" ? selectedItem.props.id : null;

  const canGenerate =
    Boolean(src) &&
    !isStudioPendingImageSrc(src) &&
    Boolean(blockId) &&
    !generating &&
    !translating;
  const englishAlt = blockId ? getEnglishBlockProp(blockId, "alt") : null;
  const canTranslate =
    !isDefaultStudioEditingLang(editingLang) &&
    Boolean(englishAlt) &&
    Boolean(blockId) &&
    !generating &&
    !translating;

  async function handleGenerate() {
    if (!canGenerate || !blockId) return;
    setGenerating(true);
    setError(null);
    try {
      const response = await fetch("/api/studio/generate-alt", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageUrl: src }),
      });
      const payload = (await response.json()) as {
        alt?: string;
        alts?: Partial<Record<SupportedLanguage, string>>;
        error?: string;
      };
      if (!response.ok || !payload.alts) {
        throw new Error(payload.error ?? "generate_alt_failed");
      }
      applyBlockPropByLocale(blockId, "alt", payload.alts);
    } catch (generateError) {
      setError(
        generateError instanceof Error
          ? generateError.message
          : "generate_alt_failed",
      );
    } finally {
      setGenerating(false);
    }
  }

  async function handleTranslate() {
    if (!canTranslate || !englishAlt || !blockId) return;
    setTranslating(true);
    setError(null);
    try {
      const response = await fetch("/api/studio/translate-text", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: englishAlt,
          targetLang: editingLang,
        }),
      });
      const payload = (await response.json()) as {
        text?: string;
        error?: string;
      };
      if (!response.ok || !payload.text) {
        throw new Error(payload.error ?? "translate_failed");
      }
      onChange(payload.text);
    } catch (translateError) {
      setError(
        translateError instanceof Error
          ? translateError.message
          : "translate_failed",
      );
    } finally {
      setTranslating(false);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <Input
        value={typeof value === "string" ? value : ""}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Describe the image for screen readers"
        className="font-monument text-xs"
        aria-label="Alt text"
      />
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => void handleGenerate()}
          disabled={!canGenerate}
          title={
            !src
              ? "Add an image URL or upload first"
              : isStudioPendingImageSrc(src)
                ? "Upload the image before generating alt text"
                : !blockId
                  ? "Select the image block first"
                  : "Generate alt text for every locale with AI"
          }
          className="rounded-md border border-black/15 px-3 py-1.5 font-monument text-xs disabled:opacity-50"
        >
          {generating ? "Generating…" : "Generate alt"}
        </button>
        {!isDefaultStudioEditingLang(editingLang) ? (
          <button
            type="button"
            onClick={() => void handleTranslate()}
            disabled={!canTranslate}
            title={
              englishAlt
                ? `Translate English alt into ${editingLang}`
                : "Add English alt text first"
            }
            className="rounded-md border border-black/15 px-3 py-1.5 font-monument text-xs disabled:opacity-50"
          >
            {translating ? "Translating…" : "Translate alt"}
          </button>
        ) : null}
      </div>
      {error ? (
        <p className="font-monument text-xs text-red-600" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
